import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  FileText,
  FileImage,
  File,
  Upload,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  ZoomIn,
  ZoomOut,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getApplicationDocuments,
  getDocumentDownloadUrl,
  type ApplicationDocument
} from '@/services/loanDocumentService';
import { PDFViewer } from './PDFViewer';
import { supabase } from '@/utils/supabase';

interface DocumentsPanelProps {
  contactId: string;
  encompassLoanId?: string;
  onUpload?: () => void;
}

// Document classification type labels
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  income_w2: 'W-2',
  income_paystub: 'Pay Stub',
  income_tax_return: 'Tax Return',
  bank_statement: 'Bank Statement',
  investment_statement: 'Investment Statement',
  retirement_statement: 'Retirement Statement',
  identification_dl: 'Driver\'s License',
  identification_passport: 'Passport',
  property_appraisal: 'Appraisal',
  property_title: 'Title',
  employment_letter: 'Employment Letter',
  loan_estimate: 'Loan Estimate',
  unknown: 'Unknown'
};

// Get icon for file type
const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') {
return FileText;
}
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) {
return FileImage;
}
  return File;
};

// Get confidence color
const getConfidenceColor = (confidence: number) => {
  if (confidence >= 0.9) {
return 'text-green-600 bg-green-50';
}
  if (confidence >= 0.7) {
return 'text-yellow-600 bg-yellow-50';
}
  return 'text-red-600 bg-red-50';
};

// Get status icon
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'classified':
    case 'completed':
      return <CheckCircle2 className="h-3 w-3 text-green-600" />;
    case 'processing':
    case 'extracting':
    case 'classifying':
      return <Clock className="h-3 w-3 text-blue-600 animate-pulse" />;
    case 'error':
      return <AlertCircle className="h-3 w-3 text-red-600" />;
    default:
      return null;
  }
};

export const DocumentsPanel: React.FC<DocumentsPanelProps> = ({
  contactId,
  encompassLoanId,
  onUpload
}) => {
  const [documents, setDocuments] = useState<ApplicationDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<ApplicationDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Load documents
  const loadDocuments = useCallback(async () => {
    if (!encompassLoanId) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const docs = await getApplicationDocuments(encompassLoanId);
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [encompassLoanId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Load preview URL when document is selected
  const handleSelectDocument = async (doc: ApplicationDocument) => {
    setSelectedDocument(doc);
    setPreviewLoading(true);
    setPreviewUrl(null);

    try {
      const url = await getDocumentDownloadUrl(doc.storageUrl);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Failed to get preview URL:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle download
  const handleDownload = async (doc: ApplicationDocument) => {
    try {
      const url = await getDocumentDownloadUrl(doc.storageUrl);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  // Get document type label
  const getTypeLabel = (doc: ApplicationDocument) => {
    if (doc.classification?.type) {
      return DOCUMENT_TYPE_LABELS[doc.classification.type] || doc.classification.type;
    }
    return 'Processing...';
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) {
return `${bytes} B`;
}
    if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(1)} KB`;
}
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Check if file is previewable
  const isPreviewable = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ext === 'pdf' || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '');
  };

  return (
    <div className={cn(
      "flex border-t",
      isFullscreen ? "fixed inset-0 z-50 bg-background" : "h-[500px]"
    )}>
      {/* Document List - Left Panel */}
      <div className={cn(
        "border-r flex flex-col",
        isFullscreen ? "w-80" : "w-72"
      )}>
        {/* List Header */}
        <div className="p-3 border-b flex items-center justify-between bg-muted/50">
          <span className="text-sm font-medium">
            Documents ({documents.length})
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onUpload}
            className="h-7 text-xs"
          >
            <Upload className="h-3 w-3 mr-1" />
            Upload
          </Button>
        </div>

        {/* Document List */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <FileText className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No documents yet</p>
              {!encompassLoanId && (
                <p className="text-xs mt-1">Link a loan to see documents</p>
              )}
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {documents.map((doc) => {
                const FileIcon = getFileIcon(doc.fileName);
                const isSelected = selectedDocument?.id === doc.id;
                const confidence = doc.classification?.confidence || 0;

                return (
                  <button
                    key={doc.id}
                    onClick={() => handleSelectDocument(doc)}
                    className={cn(
                      "w-full text-left p-2 rounded-md transition-colors",
                      "hover:bg-muted/80",
                      isSelected && "bg-primary/10 border border-primary/20"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <FileIcon className={cn(
                        "h-4 w-4 mt-0.5 flex-shrink-0",
                        isSelected ? "text-primary" : "text-muted-foreground"
                      )} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-medium truncate">
                            {doc.fileName}
                          </span>
                          {getStatusIcon(doc.status)}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            variant="secondary"
                            className={cn("text-[10px] px-1.5 py-0", getConfidenceColor(confidence))}
                          >
                            {getTypeLabel(doc)}
                          </Badge>
                          {confidence > 0 && (
                            <span className={cn(
                              "text-[10px]",
                              getConfidenceColor(confidence).split(' ')[0]
                            )}>
                              {Math.round(confidence * 100)}%
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {formatFileSize(doc.fileSize)}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Preview Panel - Right */}
      <div className="flex-1 flex flex-col bg-muted/30">
        {/* Preview Header */}
        <div className="p-3 border-b flex items-center justify-between bg-background">
          {selectedDocument ? (
            <>
              <span className="text-sm font-medium truncate flex-1">
                {selectedDocument.fileName}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(selectedDocument)}
                  className="h-7 w-7 p-0"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="h-7 w-7 p-0"
                  title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                >
                  {isFullscreen ? (
                    <X className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">Preview</span>
          )}
        </div>

        {/* Preview Content */}
        <div className="flex-1 flex items-center justify-center overflow-hidden">
          {!selectedDocument ? (
            <div className="flex flex-col items-center text-muted-foreground">
              <FileText className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-sm">Select a document to preview</p>
            </div>
          ) : previewLoading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-2">Loading preview...</p>
            </div>
          ) : !isPreviewable(selectedDocument.fileName) ? (
            <div className="flex flex-col items-center text-muted-foreground">
              <File className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-sm">Preview not available for this file type</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownload(selectedDocument)}
                className="mt-3"
              >
                <Download className="h-4 w-4 mr-2" />
                Download to View
              </Button>
            </div>
          ) : previewUrl ? (
            selectedDocument.fileName.toLowerCase().endsWith('.pdf') ? (
              <PDFViewer
                url={previewUrl}
                className="w-full h-full"
              />
            ) : (
              <img
                src={previewUrl}
                alt={selectedDocument.fileName}
                className="max-w-full max-h-full object-contain"
              />
            )
          ) : (
            <div className="flex flex-col items-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-sm">Failed to load preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsPanel;
